import { inject, Injectable, Signal } from '@angular/core';
import {
  hasShopifySalesChannel,
  selectDefaultStoreSalesChannelIntegrations,
} from '@app/main-store/integrations/integrations.selectors';
import { translateSignal } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { ApiIntegration, DataObject } from '../interfaces';

export type RouteGroup =
  | 'home'
  | 'accounting'
  | 'bank'
  | 'store-settings'
  | 'company-settings'
  | 'tools'
  | 'crm'
  | 'invoicing'
  | null;
export type AccountingGroup =
  | 'order-view'
  | 'orders'
  | 'payments'
  | 'refundTransactions'
  | 'corrections'
  | 'bundles'
  | 'balance'
  | 'payouts'
  | 'returns'
  | 'account-balance'
  | 'reports'
  | 'accounting-entries'
  | 'crm'
  | 'invoicing'
  | null;

@Injectable({
  providedIn: 'root',
})
export class RouteMappingService {
  private rxStore = inject(Store);
  public hasShopifySalesChannel$: Observable<boolean> = this.rxStore.select(hasShopifySalesChannel);
  public salesChannelPlatform: Signal<DataObject<ApiIntegration>[]> = this.rxStore.selectSignal(
    selectDefaultStoreSalesChannelIntegrations,
  );

  // Translation signals
  private ordersLabel = translateSignal('navigation.tabs.orders');
  private refundsLabel = translateSignal('navigation.tabs.refunds');
  private fulfillmentsLabel = translateSignal('navigation.tabs.fulfillments');
  private returnsLabel = translateSignal('navigation.tabs.returns');
  private paymentGatewayLabel = translateSignal('navigation.tabs.paymentGateway');
  private salesChannelCashLabel = translateSignal('navigation.tabs.salesChannelCash');
  private salesChannelBankTransferLabel = translateSignal('navigation.tabs.salesChannelBankTransfer');
  private salesChannelOtherLabel = translateSignal('navigation.tabs.salesChannelOther');
  private giftCardLabel = translateSignal('navigation.tabs.giftCard');
  private postingBalanceLabel = translateSignal('navigation.tabs.postingBalance');
  private salesChannelGatewayLabel = translateSignal('navigation.tabs.salesChannelGateway');
  private customersLabel = translateSignal('navigation.tabs.customers');
  private shopCompaniesLabel = translateSignal('navigation.tabs.shopCompanies');
  private emailLabel = translateSignal('general.email');
  private eanLabel = translateSignal('general.ean');

  private readonly routeGroups = new Map<RouteGroup, string[]>([
    ['store-settings', ['settings/store', 'integrations']],
    [
      'accounting',
      [
        'orders',
        'payments',
        'refunds',
        'refund-transactions',
        'payouts',
        'corrections',
        'bundles',
        'fulfillments',
        'returns',
        'accounting-entries',
      ],
    ],
    ['bank', ['bank']],
    ['tools', ['']],
    ['company-settings', ['settings/company', 'account']],
    ['crm', ['customers', 'shop-companies']],
    ['invoicing', ['mailed-documents/ean', 'mailed-documents/email']],
    ['home', ['']],
  ]);

  private readonly accountingGroups = new Map<AccountingGroup, string[]>([
    ['order-view', ['/orders/']],
    ['orders', ['/orders', '/refunds', '/fulfillments', '/returns']],
    [
      'payments',
      [
        '/payments/gateway',
        '/payments/saleschannel/cash',
        '/payments/giftcard',
        '/payments/saleschannel/bank_transfer',
        '/payments/saleschannel/gateway',
        '/payments/saleschannel/other',
      ],
    ],
    [
      'refundTransactions',
      [
        '/refund-transactions/gateway',
        '/refund-transactions/saleschannel/cash',
        '/refund-transactions/giftcard',
        '/refund-transactions/saleschannel/bank_transfer',
        '/refund-transactions/saleschannel/gateway',
        '/refund-transactions/saleschannel/other',
      ],
    ],
    ['corrections', ['/corrections']],
    ['bundles', ['/bundles']],
    ['payouts', ['/payouts']],
    ['accounting-entries', ['/accounting-entries']],
    ['crm', ['/customers', '/shop-companies']],
    ['invoicing', ['/mailed-documents/ean', '/mailed-documents/email']],
  ]);

  public get orderTabGroupItems() {
    return [
      { label: this.ordersLabel(), route: '/orders' },
      { label: this.refundsLabel(), route: '/refunds' },
      { label: this.fulfillmentsLabel(), route: '/fulfillments', requiresShopify: true },
      { label: this.returnsLabel(), route: '/returns', requiresShopify: true },
    ];
  }

  public get paymentsTabGroupItems() {
    return [
      { label: this.paymentGatewayLabel(), route: '/payments/gateway' },
      { label: this.salesChannelCashLabel(), route: '/payments/saleschannel/cash' },
      { label: this.giftCardLabel(), route: '/payments/giftcard' },
      { label: this.salesChannelBankTransferLabel(), route: '/payments/saleschannel/bank_transfer' },
      {
        label: this.salesChannelOtherLabel(),
        route: '/payments/saleschannel/other',
      },
    ];
  }

  public get paymentsGatewayTabGroupItems() {
    return [
      {
        label: this.salesChannelGatewayLabel(),
        route: '/payments/saleschannel/gateway',
      },
    ];
  }

  public get refundTransactionsTabGroupItems() {
    return [
      { label: this.paymentGatewayLabel(), route: '/refund-transactions/gateway' },
      { label: this.salesChannelCashLabel(), route: '/refund-transactions/saleschannel/cash' },
      { label: this.giftCardLabel(), route: '/refund-transactions/giftcard' },
      { label: this.salesChannelBankTransferLabel(), route: '/refund-transactions/saleschannel/bank_transfer' },
      { label: this.salesChannelOtherLabel(), route: '/refund-transactions/saleschannel/other' },
    ];
  }

  public get refundTransactionsGatewayTabGroupItems() {
    return [{ label: this.salesChannelGatewayLabel(), route: '/refund-transactions/saleschannel/gateway' }];
  }

  public get balanceTabGroupItems() {
    return [{ label: this.postingBalanceLabel(), route: '/orders-postings' }];
  }

  public get crmTabGroupItems() {
    return [
      { label: this.customersLabel(), route: '/customers' },
      { label: this.shopCompaniesLabel(), route: '/shop-companies', requiresShopify: true },
    ];
  }

  public get invoicingGroupItems() {
    return [
      { label: this.emailLabel(), route: '/mailed-documents/email' },
      { label: this.eanLabel(), route: '/mailed-documents/ean' },
    ];
  }

  public get tabGroupItemsMap() {
    return {
      orders: this.orderTabGroupItems,
      payments: this.paymentsTabGroupItems,
      refundTransactions: this.refundTransactionsTabGroupItems,
      balance: this.balanceTabGroupItems,
      invoicing: this.invoicingGroupItems,
      crm: this.crmTabGroupItems,
    };
  }

  public get gatewayTabGroupItemsMap() {
    return {
      payments: this.paymentsGatewayTabGroupItems,
      refundTransactions: this.refundTransactionsGatewayTabGroupItems,
    };
  }

  getRouteGroup(url: string): RouteGroup {
    const cleanUrl = this.removeQueryParameters(url);

    if (cleanUrl === '/') return 'home';
    if (cleanUrl === '/tools' || cleanUrl.startsWith('/tools/')) return 'tools';

    for (const [group, paths] of this.routeGroups.entries()) {
      for (const path of paths) {
        if (cleanUrl === `/${path}` || cleanUrl.includes(`/${path}/`)) {
          return group;
        }
      }
    }

    return null;
  }

  getAccountingGroup(url: string): AccountingGroup {
    const cleanUrl = this.removeQueryParameters(url);

    return (
      Array.from(this.accountingGroups.entries()).find(([_, paths]) =>
        paths.some((path) => cleanUrl.startsWith(path)),
      )?.[0] || null
    );
  }

  getTabGroupItems(accountingGroup: AccountingGroup): any[] {
    return this.tabGroupItemsMap[accountingGroup as keyof typeof this.tabGroupItemsMap] || [];
  }

  getGatewayTabGroupItems(accountingGroup: AccountingGroup): any[] {
    return this.gatewayTabGroupItemsMap[accountingGroup as keyof typeof this.gatewayTabGroupItemsMap] || [];
  }

  removeQueryParameters(url: string): string {
    if (!url) return '/';
    const [baseUrl, hash] = url.split('#');
    const cleanUrl = baseUrl.split('?')[0];

    return hash ? `${cleanUrl}#${hash}` : cleanUrl;
  }
}
